import { useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Link2, Printer, RotateCcw } from 'lucide-react';
import { monthLabel } from '../../utils/useStatementModel';

interface CommandBarProps {
  month: string;
  sharePct: number;
  dirty: boolean;
  onMonthStep: (delta: 1 | -1) => void;
  onSharePct: (pct: number) => void;
  onReset: () => void;
  onExportCsv: () => void;
  onPrint: () => void;
  onCopyLink: () => void;
}

/** Month stepper, global share % (run-time override, never persisted), exports. */
export function CommandBar({
  month,
  sharePct,
  dirty,
  onMonthStep,
  onSharePct,
  onReset,
  onExportCsv,
  onPrint,
  onCopyLink,
}: CommandBarProps) {
  const [copied, setCopied] = useState(false);

  function copyLink() {
    onCopyLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex flex-wrap items-center gap-3 print:hidden">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMonthStep(-1)}
          className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[8.5rem] text-center text-base font-semibold text-gray-900">
          {monthLabel(month)}
        </span>
        <button
          type="button"
          onClick={() => onMonthStep(1)}
          className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <label className="flex items-center gap-1.5 text-sm text-gray-600">
        Share %
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={Number((sharePct * 100).toFixed(2))}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v) && v >= 0 && v <= 100) onSharePct(v / 100);
          }}
          className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm tabular-nums focus:border-emerald-500 focus:outline-none"
        />
      </label>

      {dirty && (
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
          title="Reset all overrides, exclusions, and adjustments"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reset
        </button>
      )}

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={onExportCsv}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
        <button
          type="button"
          onClick={onPrint}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Printer className="h-4 w-4" /> PDF
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Link2 className="h-4 w-4" /> {copied ? 'Copied!' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}
